import React, { createRef, useEffect, useState } from 'react';
import { Box, styled } from '@mui/material';
import { QuantitySelector } from '../../shared/QuantitySelector';
import { CollectionProductTitle } from '../CollectionProductItem/CollectionProductTitle';
import { CollectionProductStockStatus } from '../CollectionProductItem/CollectionProductStockStatus';
import { CollectionProductPrice } from '../CollectionProductItem/CollectionProductNetPrice';
import { ProductAddToCartButton } from '../../shared/ProductAddToCartButton';
import { CollectionProductImage } from '../CollectionProductItem/CollectionProductImage';
import { CollectionProductItemProps } from '../../../models/Collection';
import styledComponent from 'styled-components';

const StyledImage = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    img: {
        maxWidth: '100%',
    },
}));

const StyledWrapper = styled(Box)(() => ({
    border: '1px solid #f5f5f5',
    borderRadius: '5px',
    height: '100%',
    padding: '10px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: '20px',
}));

const StyledCardFooter = styledComponent.div<{ isRow?: boolean }>`
    display: flex;
    flex-direction: ${(props) => (props.isRow ? 'row' : 'column')};
    gap: 12px;
    align-items: center;
`;

const StyledCardContent = styled(Box)(() => ({
    display: 'flex',
    gap: '10px',
    alignItems: 'start',
    flexWrap: 'wrap',
    ' > *': {
        flex: '1',
    },
}));

const StyledContainer = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
}));

export const CollectionGridProductItem = (
    props: CollectionProductItemProps
) => {
    const { product, handle } = props;

    const [qty, setQty] = useState(1);

    const [isSmall, setSmall] = useState<boolean>(false);

    const containerRef = createRef<HTMLDivElement>();

    const handleChange = (qty): void => setQty(qty);

    // this useEffect hook calculates if the card isSmall or not | width is less than 140px
    useEffect(() => {
        if (!containerRef.current) return;
        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                if (entry.contentRect.width < 200) {
                    setSmall(true);
                    return;
                }

                setSmall(false);
            }
        });
        resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect(); // clean up
    }, [containerRef]);

    return (
        <StyledWrapper ref={containerRef}>
            <StyledImage>
                <CollectionProductImage imageSrc={product.image} />
            </StyledImage>
            <StyledContainer>
                <Box sx={{ justifyContent: 'center' }}>
                    <CollectionProductTitle
                        product={product}
                        collectionHandle={handle}
                    />
                </Box>
                <StyledCardContent>
                    <Box sx={{ alignItems: 'flex-start' }}>
                        <CollectionProductStockStatus
                            quantity={product.stock}
                        />
                    </Box>

                    <CollectionProductPrice price={product.price} />
                </StyledCardContent>
                <StyledCardFooter isRow={!isSmall}>
                    <Box>
                        <QuantitySelector
                            qtyChanged={handleChange}
                            minOrderQuantity={product.minOrderQuantity}
                            minOrderMultiplier={product.minOrderMultiplier == 1}
                        />
                    </Box>
                    <ProductAddToCartButton
                        variant={product.shopifyVariantId}
                        quantity={qty}
                        availableQty={product.quantityAvailable}
                    />
                </StyledCardFooter>
            </StyledContainer>
        </StyledWrapper>
    );
};
